import AddIcon from '@mui/icons-material/Add';
import FacebookIcon from '@mui/icons-material/Facebook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WebhookIcon from '@mui/icons-material/Webhook';
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChannelFormData } from './ChannelModal';
import { DeliveryTargetPickerModal } from './DeliveryTargetPickerModal';

// ---------------------------------------------------------
// Delivery Targets Section
// ---------------------------------------------------------

const getTargetIcon = (driver: string) => {
  switch (driver?.toLowerCase()) {
    case 'appmessages':
    case 'mobileapp':
      return PhoneAndroidIcon;
    case 'facebook':
      return FacebookIcon;
    case 'webhook':
      return WebhookIcon;
    case 'pushnotification':
      return NotificationsActiveIcon;
    default:
      return NotificationsActiveIcon;
  }
};

const getDriverLabel = (driver: string, t: (key: string) => string) => {
  switch (driver?.toLowerCase()) {
    case 'appmessages':
      return t('blox.notifier.channel.targets.mobile_app');
    case 'facebook':
      return t('blox.notifier.channel.targets.facebook');
    case 'webhook':
      return t('blox.notifier.channel.targets.webhook');
    case 'pushnotification':
      return t('blox.notifier.channel.targets.push_notification');
    default:
      return driver;
  }
};

export const DeliveryTargetsSection = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { watch, setValue } = useFormContext<ChannelFormData>();
  const [pickerOpen, setPickerOpen] = useState(false);

  const strDeliveryTargets = t('blox.notifier.channel.delivery_targets');
  const strAddTargets = t('blox.notifier.channel.add_targets');
  const strManageTargets = t('blox.notifier.channel.manage_targets');
  const strNoTargetsSelected = t('blox.notifier.channel.no_targets_selected');

  const watchedTargets = watch('targets');
  const selectedTargets = useMemo(() => watchedTargets || [], [watchedTargets]);

  // Group selected targets by driver for display
  const groupedSelected = useMemo(() => {
    return selectedTargets.reduce(
      (acc, target) => {
        const driver = target.driver || 'other';
        if (!acc[driver]) {
          acc[driver] = [];
        }
        acc[driver].push(target);
        return acc;
      },
      {} as Record<string, typeof selectedTargets>,
    );
  }, [selectedTargets]);

  const handleRemoveTarget = useCallback(
    (targetId: string) => {
      setValue(
        'targets',
        selectedTargets.filter((t) => t.id !== targetId),
        { shouldDirty: true },
      );
    },
    [selectedTargets, setValue],
  );

  const handleAcceptTargets = useCallback(
    (targets: { id: string; driver: string }[]) => {
      setValue('targets', targets, { shouldDirty: true });
    },
    [setValue],
  );

  const hasTargets = selectedTargets.length > 0;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle2">{strDeliveryTargets}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerOpen(true)}
        >
          {hasTargets ? strManageTargets : strAddTargets}
        </Button>
      </Stack>

      {!hasTargets ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {strNoTargetsSelected}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {Object.entries(groupedSelected).map(([driver, targets]) => {
            const Icon = getTargetIcon(driver);
            return (
              <Box
                key={driver}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Icon
                    fontSize="small"
                    color="action"
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {getDriverLabel(driver, t)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    ({targets.length})
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {targets.map((target) => (
                    <Chip
                      key={target.id}
                      label={target.name || target.id}
                      size="small"
                      variant="outlined"
                      onDelete={() => handleRemoveTarget(target.id)}
                      sx={{
                        height: 'auto',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                        },
                        '& .MuiChip-deleteIcon': {
                          '&:hover': {
                            color: theme.palette.error.main,
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Delivery Target Picker Modal */}
      <DeliveryTargetPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedTargets={selectedTargets}
        onAccept={handleAcceptTargets}
      />
    </Box>
  );
});
DeliveryTargetsSection.displayName = 'DeliveryTargetsSection';
